import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import {
  Clear as ClearIcon,
  FileDownloadOutlined as ExportIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { toast } from "react-hot-toast";
import dayjs from "dayjs";
import { Workbook } from "exceljs";
import { saveAs } from "file-saver";
import { PaginatedTable } from "../../components";
import { baseAxios, Context } from "../../utils/config.js";
import { colors } from "../../theme/colors";

const MARK_STYLE = {
  backgroundColor: "rgba(197, 160, 111, 0.45)",
  color: "inherit",
  borderRadius: 4,
};

const TRANSACTION_COLUMNS = [
  {
    id: "salon",
    label: "Salon",
    field: "location_name",
    minWidth: 190,
  },
  { id: "user_name", label: "Stylist", field: "user_name", minWidth: 140 },
  {
    id: "transaction_client_name",
    label: "Client Name",
    field: "transaction_client_name",
    minWidth: 150,
  },
  {
    id: "transaction_start",
    label: "Date",
    field: "transaction_start",
    minWidth: 145,
  },
  {
    id: "product_alias",
    label: "Product",
    field: "product_alias",
    minWidth: 240,
  },
  {
    id: "transaction_entry_product_amount",
    label: "Amount",
    field: "transaction_entry_product_amount",
    minWidth: 120,
  },
  {
    id: "transaction_waste",
    label: "Waste",
    field: "transaction_waste",
    minWidth: 110,
  },
  {
    id: "product_cost",
    label: "Product Cost",
    field: "product_cost",
    minWidth: 120,
  },
];

function HighlightText({ text, query }) {
  const str = String(text ?? "");
  const q = String(query ?? "").trim();
  if (!q || !str) return str || null;

  const escapeRegex = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const tokens = q
    .split(/\s+/)
    .filter(Boolean)
    .filter((t) => t.length > 1)
    .map(escapeRegex);

  const phrase = q.includes(" ") ? escapeRegex(q) : null;
  const patterns = [phrase, ...tokens].filter(Boolean);
  if (!patterns.length) return str;

  let pattern;
  try {
    pattern = new RegExp(`(${patterns.join("|")})`, "i");
  } catch {
    return str;
  }

  const parts = str.split(pattern);
  if (parts.length === 1) return str;

  return parts.map((part, i) =>
    pattern.test(part) ? (
      <mark key={i} style={MARK_STYLE}>
        {part}
      </mark>
    ) : (
      part
    ),
  );
}

function formatDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("en-us", {
    year: "2-digit",
    month: "numeric",
    day: "numeric",
  });
}

function formatTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleTimeString("en-us", {
    hour: `numeric`,
    minute: `2-digit`,
  });
}

function formatNumber(value) {
  const number = Number(value);
  if (Number.isNaN(number)) return value ?? "";
  return number.toFixed(2);
}

function formatCurrency(value) {
  const number = Number(value);
  if (Number.isNaN(number) || number < 0.01) return "$0";
  return `$${number.toFixed(2)}`;
}

function gramsLabel(value) {
  const formatted = formatNumber(value);
  return formatted ? `${formatted} g` : "-";
}

function formatProductDescription(value) {
  const text = String(value ?? "").trim();
  if (/^register cash\s*out$/i.test(text) || /^payout$/i.test(text)) {
    return "Register Payout";
  }
  if (/^pay in$/i.test(text) || /^register pay in$/i.test(text)) {
    return "Register Pay In";
  }
  return text;
}

export default function Reports() {
  const { location: globalLocation } = useContext(Context);
  const [transactions, setTransactions] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState("transaction_start");
  const [sortOrder, setSortOrder] = useState("desc");
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [currentRowsPerPage, setCurrentRowsPerPage] = useState(15);
  const selectedLocationId = globalLocation?.location_id;
  const selectedLocationName =
    selectedLocationId && selectedLocationId !== -1
      ? globalLocation?.location_name
      : "";

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput), 500);
    return () => clearTimeout(t);
  }, [searchInput]);

  const fetchTransactions = useCallback(
    (page, rowsPerPage, orderBy = sortBy, order = sortOrder) => {
      setLoading(true);
      setCurrentPage(page);
      setCurrentRowsPerPage(rowsPerPage);
      const params = {
        page,
        limit: rowsPerPage,
        sortBy: orderBy,
        sortOrder: order,
      };
      if (debouncedSearch.trim()) params.search = debouncedSearch.trim();

      baseAxios
        .get("/transactions", { params })
        .then((res) => {
          setTransactions(res?.data?.transactions ?? []);
          setTotalCount(res?.data?.totalCount ?? 0);
        })
        .catch(() => {
          setTransactions([]);
          setTotalCount(0);
          toast.error("Error loading transactions", { duration: 3000 });
        })
        .finally(() => setLoading(false));
    },
    [sortBy, sortOrder, debouncedSearch],
  );

  const handleRefresh = useCallback(() => {
    fetchTransactions(currentPage, currentRowsPerPage);
  }, [fetchTransactions, currentPage, currentRowsPerPage]);

  const handleExport = useCallback(async () => {
    if (!transactions.length) {
      toast.error("No data to export", { duration: 2500 });
      return;
    }
    try {
      const workbook = new Workbook();
      const worksheet = workbook.addWorksheet("Transactions");
      worksheet.columns = [
        { header: "Salon", key: "location_name", width: 20 },
        { header: "Organization", key: "org_name", width: 18 },
        { header: "Stylist", key: "user_name", width: 16 },
        { header: "Client Name", key: "transaction_client_name", width: 18 },
        { header: "Date", key: "date", width: 12 },
        { header: "Time", key: "time", width: 12 },
        { header: "Product", key: "product_alias", width: 28 },
        { header: "Amount (g)", key: "amount", width: 12 },
        { header: "Waste (g)", key: "waste", width: 12 },
        { header: "Product Cost", key: "product_cost", width: 12 },
      ];
      worksheet.getRow(1).font = { bold: true };
      transactions.forEach((row) => {
        worksheet.addRow({
          location_name: row.location_name || "",
          org_name: row.org_name || "",
          user_name: row.user_name || "",
          transaction_client_name: row.transaction_client_name || "",
          date: formatDate(row.transaction_start),
          time: formatTime(row.transaction_entry_start || row.transaction_start),
          product_alias: formatProductDescription(row.product_alias),
          amount: Number(row.transaction_entry_product_amount) || 0,
          waste: Number(row.transaction_waste) || 0,
          product_cost: Number(row.product_cost) || 0,
        });
      });
      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(
        new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
        `Transactions_${dayjs().format("YYYY-MM-DD")}.xlsx`
      );
    } catch {
      toast.error("Export failed", { duration: 3000 });
    }
  }, [transactions]);

  const handleSortChange = useCallback(
    (newSortBy, newSortOrder, rowsPerPage) => {
      setSortBy(newSortBy);
      setSortOrder(newSortOrder);
      fetchTransactions(0, rowsPerPage, newSortBy, newSortOrder);
    },
    [fetchTransactions],
  );

  useEffect(() => {
    fetchTransactions(0, currentRowsPerPage);
  }, [debouncedSearch, selectedLocationId]);

  const columns = useMemo(
    () =>
      TRANSACTION_COLUMNS.map((col) => {
        if (col.id === "salon") {
          return {
            ...col,
            render: (_, row) => {
              const salon = row.location_name;
              const org = row.org_name;
              if (!salon && !org) return "-";
              return (
                <Box sx={{ py: 0.25 }}>
                  <Typography fontWeight={500} fontSize={14} component="div">
                    {salon ? (
                      <HighlightText text={salon} query={debouncedSearch} />
                    ) : (
                      "-"
                    )}
                  </Typography>
                  {org ? (
                    <Typography
                      fontSize={12}
                      color="text.secondary"
                      component="div"
                      sx={{ display: "block", mt: 0.25, lineHeight: 1.3 }}
                    >
                      <HighlightText text={org} query={debouncedSearch} />
                    </Typography>
                  ) : null}
                </Box>
              );
            },
          };
        }
        if (col.id === "transaction_start") {
          return {
            ...col,
            render: (value, row) => {
              const date = formatDate(value);
              const start = formatTime(row.transaction_entry_start || value);
              return (
                <Box
                  component="span"
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.75,
                  }}
                >
                  <Typography component="span" fontWeight={500} fontSize={14}>
                    <HighlightText text={date || "-"} query={debouncedSearch} />
                  </Typography>
                  {start ? (
                    <Typography
                      component="span"
                      color="text.secondary"
                      fontSize={12}
                    >
                      <HighlightText text={start} query={debouncedSearch} />
                    </Typography>
                  ) : null}
                </Box>
              );
            },
          };
        }
        if (col.id === "transaction_entry_product_amount") {
          return {
            ...col,
            render: (value) => (
              <HighlightText text={gramsLabel(value)} query={debouncedSearch} />
            ),
          };
        }
        if (col.id === "transaction_waste") {
          return {
            ...col,
            render: (value) => {
              const waste = Number(value);
              const hasWaste = !Number.isNaN(waste) && waste > 0;
              return (
                <Chip
                  size="small"
                  variant={hasWaste ? "filled" : "outlined"}
                  label={
                    <HighlightText
                      text={gramsLabel(value)}
                      query={debouncedSearch}
                    />
                  }
                  sx={{
                    height: 24,
                    fontWeight: 600,
                    bgcolor: hasWaste ? colors.gold : "transparent",
                    color: hasWaste ? colors.black : "text.secondary",
                    borderColor: hasWaste ? colors.gold : "divider",
                    "& .MuiChip-label": { px: 1 },
                  }}
                />
              );
            },
          };
        }
        if (col.id === "product_cost") {
          return {
            ...col,
            render: (value) => (
              <Typography component="span" fontWeight={600} fontSize={14}>
                <HighlightText
                  text={formatCurrency(value)}
                  query={debouncedSearch}
                />
              </Typography>
            ),
          };
        }
        return {
          ...col,
          render: (value) => {
            const text =
              col.id === "product_alias"
                ? formatProductDescription(value)
                : String(value ?? "");
            return text ? (
              <HighlightText text={text} query={debouncedSearch} />
            ) : (
              "-"
            );
          },
        };
      }),
    [debouncedSearch],
  );

  return (
    <Box sx={{ px: 8, py: 3 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          mt: 1.5,
          mb: 2,
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            <Typography fontWeight={600} sx={{ fontSize: "1.5rem" }}>
              Transactions
            </Typography>
            {selectedLocationName ? (
              <Chip
                size="small"
                label={selectedLocationName}
                sx={{
                  bgcolor: colors.black,
                  color: colors.gold,
                  fontWeight: 600,
                  borderRadius: "6px",
                }}
              />
            ) : null}
          </Box>
          <Typography variant="body2" color="text.secondary">
            Search, review, and audit raw product transactions.
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
          }}
        >
          <IconButton
            aria-label="Refresh transactions"
            onClick={handleRefresh}
            disabled={loading}
            size="medium"
            sx={{
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
              borderRadius: "6px",
              "&:hover": { bgcolor: "grey.100" },
            }}
          >
            <RefreshIcon />
          </IconButton>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<ExportIcon />}
            onClick={handleExport}
            disabled={loading || !transactions.length}
            sx={{
              minHeight: 40,
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "6px",
            }}
          >
            Export
          </Button>
          <TextField
            size="small"
            placeholder="Search salon, client, user, product, cost…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            variant="outlined"
            sx={{
              minWidth: 340,
              maxWidth: "100%",
              "& .MuiOutlinedInput-root": {
                backgroundColor: "background.paper",
                borderRadius: "6px",
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start" sx={{ mr: 0.75 }}>
                  <SearchIcon
                    sx={{
                      fontSize: 20,
                      color: "grey.400",
                    }}
                  />
                </InputAdornment>
              ),
              endAdornment: searchInput ? (
                <InputAdornment position="end" sx={{ mr: 0.75 }}>
                  <IconButton
                    size="small"
                    onClick={() => setSearchInput("")}
                    edge="end"
                    aria-label="Clear search"
                    sx={{
                      color: "grey.500",
                      "&:hover": {
                        color: "grey.800",
                        backgroundColor: "grey.200",
                      },
                    }}
                  >
                    <ClearIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
        </Box>
      </Box>

      <PaginatedTable
        columns={columns}
        rows={transactions}
        totalRowCount={totalCount}
        loading={loading}
        onPaginationChange={fetchTransactions}
        onSortChange={handleSortChange}
        initialOrderBy="transaction_start"
        initialOrder="desc"
        searchQuery={`${debouncedSearch}|${selectedLocationId ?? "all"}`}
        skeletonHeight="40px"
      />
    </Box>
  );
}
